import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMapaVR, createVRApuracao, getRegistros } from '../services/api';

const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const COR = {
  normal:             { bg: '#fff',     text: '#1e293b' },
  falta:              { bg: '#fca5a5', text: '#7f1d1d' },
  afastamento:        { bg: '#fed7aa', text: '#7c2d12' },
  ferias:             { bg: '#bfdbfe', text: '#1e3a8a' },
  atestado:           { bg: '#fef08a', text: '#713f12' },
  folga:              { bg: '#e5e7eb', text: '#6b7280' },
  feriado:            { bg: '#d1fae5', text: '#065f46' },
  feriado_trabalhado: { bg: '#a7f3d0', text: '#065f46' },
};
const LABEL = { normal:'1', falta:'F', afastamento:'A', ferias:'F', atestado:'AT', folga:'', feriado:'Fe', feriado_trabalhado:'+1' };

function prevMonthOf(mes, ano) {
  let m = mes - 1, a = ano;
  if (m < 1) { m = 12; a -= 1; }
  return { mes: m, ano: a };
}

// ─── Funções de apuração ──────────────────────────────────────────────────────

/**
 * Apura a base de VR do mês de competência (crédito).
 * - Dias úteis (seg–sex): 1 ticket
 * - Sábado previsto na jornada: 0,5 ticket
 * - Férias/afastamentos/faltas/atestados: sem direito ao VR (deduz o valor proporcional)
 * Retorna: { diasUteis, diasSemDireito, vrBase }
 */
function calcBase(func, diasList) {
  const diasSemana = new Set(func.dias_semana || ['SEG','TER','QUA','QUI','SEX']);
  const SIGLA = ['DOM','SEG','TER','QUA','QUI','SEX','SAB'];
  let diasUteis = 0;
  let diasFerias = 0;
  let diasAfastamento = 0;
  let diasSemDireito = 0;

  diasList.forEach(d => {
    const info = func.dias[d];
    if (!info || info.valor === null) return;
    if (info.tipo === 'folga') return;
    if (info.tipo === 'feriado') return;

    const sigla = SIGLA[new Date(d + 'T00:00:00').getDay()];
    const ticket = (sigla === 'SAB' && diasSemana.has('SAB')) ? 0.5 : 1;

    diasUteis += ticket;
    if (info.tipo === 'ferias') {
      diasFerias += ticket;
      diasSemDireito += ticket;
    } else if (info.tipo !== 'normal') {
      diasAfastamento += ticket;
      diasSemDireito += ticket;
    }
  });

  return { diasUteis, diasFerias, diasAfastamento, diasSemDireito, vrBase: diasUteis - diasSemDireito };
}

/**
 * Apura os descontos vindos do mês anterior (referência).
 * Retorna: { faltas, faltasJust, descontoTotal }
 *   faltas      → tipo='falta'   → cada uma desconta 1,0 ticket
 *   faltasJust  → tipo='atestado' E carga_horaria < 6h → cada uma desconta 0,5 ticket
 */
function calcDescontos(func, diasList) {
  const cargaH = Number(func.carga_horaria) || 8;
  let faltas = 0;
  let faltasJust = 0;

  diasList.forEach(d => {
    const info = func.dias[d];
    if (!info) return;

    if (info.tipo === 'falta') {
      faltas += 1;
    } else if (info.tipo === 'atestado' && cargaH < 6) {
      faltasJust += 1;
    }
  });

  const descontoTotal = faltas * 1.0 + faltasJust * 0.5;
  return { faltas, faltasJust, descontoTotal };
}

/**
 * Apura os acréscimos vindos do mês atual (referência).
 * Dia extra = funcionário trabalhou num dia que a jornada NÃO prevê
 *   (sábado/domingo fora da jornada) OU trabalhou em feriado.
 * - Jornada >= 6h → +1,0 ticket por dia extra
 * - Jornada < 6h  → +0,5 ticket por dia extra
 * Retorna: { extrasInteiro, extrasMeio, acrescimoTotal }
 */
function calcAcrescimos(func, diasList) {
  const cargaH = Number(func.carga_horaria) || 8;
  const diasSemana = new Set(func.dias_semana || ['SEG','TER','QUA','QUI','SEX']);
  const SIGLA = ['DOM','SEG','TER','QUA','QUI','SEX','SAB']; // getDay() → sigla
  let extrasInteiro = 0;
  let extrasMeio = 0;

  diasList.forEach(d => {
    const info = func.dias[d];
    if (!info) return;

    // Feriado trabalhado → sempre extra
    if (info.tipo === 'feriado_trabalhado') {
      cargaH < 6 ? extrasMeio++ : extrasInteiro++;
      return;
    }

    // Dia trabalhado normalmente fora da jornada → extra
    if (info.tipo === 'normal') {
      const sigla = SIGLA[new Date(d + 'T00:00:00').getDay()];
      if (!diasSemana.has(sigla)) {
        cargaH < 6 ? extrasMeio++ : extrasInteiro++;
      }
    }
  });

  const acrescimoTotal = extrasInteiro * 1.0 + extrasMeio * 0.5;
  return { extrasInteiro, extrasMeio, acrescimoTotal };
}

/**
 * Consolida o total final por funcionário.
 * VR final = vrBase - descontoTotal + acrescimoTotal
 */
function consolidar(baseResult, descontosResult, acrescimosResult) {
  const { diasUteis, diasFerias, diasAfastamento, diasSemDireito, vrBase } = baseResult;
  const { faltas, faltasJust, descontoTotal } = descontosResult;
  const { extrasInteiro, extrasMeio, acrescimoTotal } = acrescimosResult;
  return {
    diasUteis,
    diasFerias,
    diasAfastamento,
    diasSemDireito,
    vrBase,
    faltas,
    faltasJust,
    extrasInteiro,
    extrasMeio,
    descontoTotal,
    acrescimoTotal,
    total: Math.max(0, vrBase - descontoTotal + acrescimoTotal),
  };
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function VrApuracao() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mes, ano, departamento } = (location.state || {});

  // mes/ano = mês de competência (crédito); ref = mês anterior (ajustes)
  const ref = prevMonthOf(mes || 1, ano || new Date().getFullYear());

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [dadosRef, setDadosRef] = useState(null);
  const [dadosCredito, setDadosCredito] = useState(null);
  const [itens, setItens] = useState([]);
  const [criando, setCriando] = useState(false);
  const [vrCriado, setVrCriado] = useState(null);
  const [erroCriar, setErroCriar] = useState('');
  const [tab, setTab] = useState('resumo');

  // Modal de registro do dia
  const [modalDia, setModalDia] = useState(null); // { func, date, info }
  const [modalReg, setModalReg] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const abrirModalDia = async (func, d, info) => {
    setModalDia({ func, date: d, info });
    setModalReg(null);
    setModalLoading(true);
    try {
      const dt = new Date(d + 'T00:00:00');
      const dd = String(dt.getDate()).padStart(2,'0');
      const mm = String(dt.getMonth()+1).padStart(2,'0');
      const yyyy = dt.getFullYear();
      const r = await getRegistros({ funcionario: func.id, data_inicio: `${dd}/${mm}/${yyyy}`, data_fim: `${dd}/${mm}/${yyyy}`, page_size: 10 });
      const regs = r.data.results || r.data;
      setModalReg(regs.length > 0 ? regs[0] : null);
    } catch { setModalReg(null); } finally { setModalLoading(false); }
  };

  const fmtNum = n => n == null ? '—' : (n % 1 === 0 ? String(n) : n.toFixed(1));

  const labelRef     = ref.mes && ref.ano ? `${MESES_PT[ref.mes - 1]}/${ref.ano}` : '—';
  const labelCredito = mes && ano         ? `${MESES_PT[mes - 1]}/${ano}`           : '—';

  useEffect(() => {
    if (!mes || !ano) {
      setErro('Parâmetros de mês/ano não informados. Volte ao Mapa de VR e tente novamente.');
      return;
    }
    async function fetchData() {
      setLoading(true);
      setErro('');
      try {
        const paramsRef     = { mes: ref.mes, ano: ref.ano, ...(departamento ? { departamento } : {}) };
        const paramsCredito = { mes, ano,                   ...(departamento ? { departamento } : {}) };

        const [rRef, rCredito] = await Promise.all([
          getMapaVR(paramsRef),
          getMapaVR(paramsCredito),
        ]);

        const dRef     = rRef.data;
        const dCredito = rCredito.data;

        setDadosRef(dRef);
        setDadosCredito(dCredito);

        // Indexa mês de referência por id
        const refById = {};
        dRef.funcionarios.forEach(f => { refById[f.id] = f; });

        // Lista base = mês de competência
        const result = dCredito.funcionarios.map(func => {
          const refFunc = refById[func.id];

          const baseR      = calcBase(func, dCredito.dias);
          const descontosR = refFunc ? calcDescontos(refFunc, dRef.dias) : { faltas: 0, faltasJust: 0, descontoTotal: 0 };
          const acrescimosR = refFunc ? calcAcrescimos(refFunc, dRef.dias) : { extrasInteiro: 0, extrasMeio: 0, acrescimoTotal: 0 };

          return {
            id: func.id,
            nome: func.nome,
            ...consolidar(baseR, descontosR, acrescimosR),
          };
        });

        result.sort((a, b) => a.nome.localeCompare(b.nome));
        setItens(result);
      } catch {
        setErro('Erro ao carregar dados. Verifique a conexão e tente novamente.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []); // eslint-disable-line

  const handleCriarVR = async () => {
    setCriando(true);
    setErroCriar('');
    try {
      const payload = {
        mes_ref: ref.mes, ano_ref: ref.ano,
        mes_cred: mes,    ano_cred: ano,
        departamento: departamento || null,
        dados_ref: dadosRef,
        dados_cred: dadosCredito,
        itens,
        total_tickets: tot.total,
        total_funcionarios: itens.length,
      };
      const r = await createVRApuracao(payload);
      setVrCriado({ id: r.data.id });
    } catch (e) {
      if (e.response?.status === 409) {
        setVrCriado({ id: e.response.data.id, existente: true });
      } else {
        setErroCriar('Erro ao salvar VR. Tente novamente.');
      }
    } finally {
      setCriando(false);
    }
  };

  // ── Totalizadores ──
  const tot = itens.reduce((acc, x) => ({
    diasUteis:       acc.diasUteis       + x.diasUteis,
    diasFerias:      acc.diasFerias      + x.diasFerias,
    diasAfastamento: acc.diasAfastamento + x.diasAfastamento,
    diasSemDireito:  acc.diasSemDireito  + x.diasSemDireito,
    vrBase:          acc.vrBase          + x.vrBase,
    faltas:         acc.faltas         + x.faltas,
    faltasJust:     acc.faltasJust     + x.faltasJust,
    extrasInteiro:  acc.extrasInteiro  + x.extrasInteiro,
    extrasMeio:     acc.extrasMeio     + x.extrasMeio,
    descontoTotal:  acc.descontoTotal  + x.descontoTotal,
    acrescimoTotal: acc.acrescimoTotal + x.acrescimoTotal,
    total:          acc.total          + x.total,
  }), { diasUteis:0, diasFerias:0, diasAfastamento:0, diasSemDireito:0, vrBase:0, faltas:0, faltasJust:0, extrasInteiro:0, extrasMeio:0, descontoTotal:0, acrescimoTotal:0, total:0 });

  const TH = ({ children, color = '#93c5fd', center = true, ...rest }) => (
    <th style={{
      padding: '9px 10px',
      textAlign: center ? 'center' : 'left',
      fontWeight: 700,
      color,
      background: '#1a2e4a',
      borderRight: '1px solid #2d4060',
      fontSize: 'var(--fs-2xs)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      whiteSpace: 'nowrap',
      verticalAlign: 'bottom',
      ...rest,
    }}>{children}</th>
  );

  const Pill = ({ val, bg, color, prefix = '' }) => val > 0
    ? <span style={{ background: bg, color, borderRadius: 4, padding: '1px 8px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{prefix}{fmtNum(val)}</span>
    : <span style={{ color: '#cbd5e1' }}>—</span>;

  return (
    <div>
      {/* ── Cabeçalho ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingBottom:16, marginBottom:20, borderBottom:'1px solid #f1f5f9' }}>
        <div style={{ display:'flex', alignItems:'center' }}>
          <div style={{ width:38, height:38, background:'#faf5ff', border:'1px solid #d8b4fe', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#7c3aed', fontSize:'1rem', marginRight:14, flexShrink:0 }}>
            <i className="bi bi-calculator-fill" />
          </div>
          <div>
            <div style={{ fontSize:'var(--fs-2xs)', fontWeight:600, color:'var(--text-subtle)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:3 }}>
              Gestão de Pessoal · Controle de Ponto
            </div>
            <div style={{ fontSize:'1.3rem', fontWeight:700, color:'#334155', letterSpacing:'-0.5px', lineHeight:1.2 }}>
              Apuração de VR
            </div>
          </div>
        </div>
        <button className="btn-secondary-custom" onClick={() => navigate('/relatorio-vr')}>
          <i className="bi bi-arrow-left" /> Voltar
        </button>
      </div>

      {/* ── Resumo dos períodos ── */}
      {!loading && !erro && dadosRef && dadosCredito && (
        <div style={{ display:'flex', gap:10, marginBottom:20 }}>
          <div style={{ flex:1, background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, background:'#fef9c3', border:'1px solid #fde68a', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#92400e', fontSize:'var(--fs-base)', flexShrink:0 }}>
              <i className="bi bi-calendar-minus" />
            </div>
            <div>
              <div style={{ fontSize:'var(--fs-2xs)', fontWeight:700, color:'#92400e', textTransform:'uppercase', letterSpacing:'0.5px' }}>Mês Atual</div>
              <div style={{ fontSize:'var(--fs-md)', fontWeight:700, color:'#1e293b' }}>{labelRef}</div>
              <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', marginTop:1 }}>Faltas · Jornadas a menor · Extras</div>
            </div>
          </div>
          <div style={{ flex:1, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, background:'#dbeafe', border:'1px solid #bfdbfe', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#1d4ed8', fontSize:'var(--fs-base)', flexShrink:0 }}>
              <i className="bi bi-calendar-check" />
            </div>
            <div>
              <div style={{ fontSize:'var(--fs-2xs)', fontWeight:700, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.5px' }}>Mês de Competência (base)</div>
              <div style={{ fontSize:'var(--fs-md)', fontWeight:700, color:'#1e293b' }}>{labelCredito}</div>
              <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', marginTop:1 }}>Dias úteis · Férias · Afastamentos</div>
            </div>
          </div>
          <div style={{ flex:1, background:'#faf5ff', border:'1px solid #d8b4fe', borderRadius:8, padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, background:'#ede9fe', border:'1px solid #d8b4fe', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#7c3aed', fontSize:'var(--fs-base)', flexShrink:0 }}>
              <i className="bi bi-receipt" />
            </div>
            <div>
              <div style={{ fontSize:'var(--fs-2xs)', fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.5px' }}>Total de Tickets</div>
              <div style={{ fontSize:'1.4rem', fontWeight:800, color:'#4f46e5', lineHeight:1.1, letterSpacing:'-0.5px' }}>{fmtNum(tot.total)}</div>
              <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', marginTop:1 }}>{itens.length} funcionário(s)</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', padding:'56px 0', color:'var(--text-muted)' }}>
          <span className="spinner-border spinner-border-sm me-2" />
          <span>Carregando dados...</span>
        </div>
      )}

      {/* ── Erro ── */}
      {erro && (
        <div className="alert-inline alert-inline-danger">
          <i className="bi bi-exclamation-triangle" />{erro}
        </div>
      )}

      {/* ── Abas ── */}
      {!loading && !erro && itens.length > 0 && (
        <div style={{ display:'flex', gap:6, marginBottom:16 }}>
          {[
            { key:'referencia',  icon:'bi-calendar-minus', label:'Mês Atual',    sub: labelRef     },
            { key:'competencia', icon:'bi-calendar-check', label:'Competência',  sub: labelCredito },
            { key:'resumo',      icon:'bi-receipt',        label:'Resumo',       sub: 'Cálculo final' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              display:'inline-flex', alignItems:'center', gap:6, padding:'6px 14px',
              borderRadius:8, fontWeight:600, fontSize:'var(--fs-xs)', cursor:'pointer',
              border: tab===t.key ? '2px solid #d8b4fe' : '1px solid #e2e8f0',
              background: tab===t.key ? '#7c3aed' : '#f8fafc',
              color: tab===t.key ? '#fff' : '#475569',
              transition:'all 0.15s',
            }}>
              <i className={`bi ${t.icon}`} />{t.label}
              <span style={{ fontSize:'var(--fs-2xs)', opacity:0.75, marginLeft:2 }}>({t.sub})</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Grid Mês Atual / Competência ── */}
      {!loading && !erro && itens.length > 0 && (tab === 'referencia' || tab === 'competencia') && (() => {
        const dados = tab === 'referencia' ? dadosRef : dadosCredito;
        const labelTab = tab === 'referencia' ? labelRef : labelCredito;
        if (!dados || !dados.funcionarios) return null;
        const diasList = dados.dias || [];
        return (
          <div className="card-ponto p-0">
            <div style={{ padding:'10px 16px', borderBottom:'1px solid #f1f5f9', background:'#f8fafc', fontSize:'var(--fs-xs)', color:'var(--text-muted)', display:'flex', flexWrap:'wrap', gap:12 }}>
              {Object.entries(COR).map(([tipo, cor]) => (
                <span key={tipo} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  <span style={{ width:16, height:16, background:cor.bg, border:'1px solid #d1d5db', borderRadius:2, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.55rem', fontWeight:700, color:cor.text }}>
                    {LABEL[tipo]}
                  </span>
                  {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                </span>
              ))}
              <span style={{ marginLeft:'auto', fontWeight:600, color:'#1e293b' }}>{labelTab}</span>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:'var(--fs-xs)', width:'100%', tableLayout:'fixed' }}>
                <colgroup>
                  <col style={{ width:200 }} />
                  {diasList.map(d => <col key={d} style={{ width:22 }} />)}
                  <col style={{ width:46 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ position:'sticky', left:0, zIndex:2, background:'#1a2e4a', color:'#fff', padding:'6px 8px 6px 24px', fontWeight:700, borderRight:'1px solid #334', fontSize:'var(--fs-xs)', whiteSpace:'nowrap', verticalAlign:'bottom' }}>
                      Nome do Funcionário
                    </th>
                    {diasList.map(d => {
                      const dt = new Date(d + 'T00:00:00');
                      const dia = String(dt.getDate()).padStart(2,'0');
                      const mes2 = String(dt.getMonth()+1).padStart(2,'0');
                      const semana = ['dom','seg','ter','qua','qui','sex','sáb'][dt.getDay()];
                      return (
                        <th key={d} style={{ background:'#1a2e4a', color:'#fff', padding:'4px 2px', textAlign:'center', borderRight:'1px solid #2d4060', verticalAlign:'bottom' }}>
                          <div style={{ writingMode:'vertical-rl', transform:'rotate(180deg)', textAlign:'left', whiteSpace:'nowrap', fontSize:'var(--fs-2xs)', padding:'4px 2px', fontWeight:600 }}>
                            {`${dia}/${mes2}`}<br />
                            <span style={{ fontWeight:400, opacity:0.8 }}>{semana}</span>
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ background:'#1a2e4a', color:'#93c5fd', padding:'6px 4px', textAlign:'center', fontWeight:700, fontSize:'var(--fs-xs)', whiteSpace:'nowrap', verticalAlign:'bottom' }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.funcionarios.map((func, idx) => {
                    // Para o Mês Atual: TOTAL = acréscimos − descontos
                    // Para Competência: TOTAL = diasUteis calculado no frontend (sáb=0,5)
                    const item = itens.find(x => x.id === func.id);
                    let totalCelula, totalCor;
                    if (tab === 'referencia') {
                      const saldo = item ? item.acrescimoTotal - item.descontoTotal : 0;
                      totalCelula = saldo === 0 ? '—' : saldo > 0 ? `+${fmtNum(saldo)}` : fmtNum(saldo);
                      totalCor = saldo > 0 ? '#7c3aed' : saldo < 0 ? '#dc2626' : '#94a3b8';
                    } else {
                      // vrBase já desconta afastamentos/férias/faltas corretamente
                      const vrBase = item ? item.vrBase : 0;
                      totalCelula = vrBase.toFixed(2).replace('.', ',');
                      totalCor = '#1d4ed8';
                    }
                    return (
                    <tr key={func.id} style={{ background: idx%2===0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ position:'sticky', left:0, zIndex:1, background: idx%2===0 ? '#fff' : '#f8fafc', padding:'4px 8px 4px 24px', fontWeight:500, borderRight:'1px solid #e2e8f0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:200, fontSize:'var(--fs-xs)' }}>
                        {func.nome}
                      </td>
                      {diasList.map(d => {
                        const info = func.dias && func.dias[d];
                        if (!info || info.valor === null) return <td key={d} style={{ background:'#f9fafb', border:'1px solid #e5e7eb', padding:0, width:22 }} />;
                        const cor = COR[info.tipo] || COR.folga;
                        const SIGLA_MAP = ['DOM','SEG','TER','QUA','QUI','SEX','SAB'];
                        const siglaD = SIGLA_MAP[new Date(d + 'T00:00:00').getDay()];
                        const diasSemanaFunc = new Set(func.dias_semana || ['SEG','TER','QUA','QUI','SEX']);
                        const cargaH = Number(func.carga_horaria) || 8;
                        let cellLabel = LABEL[info.tipo] ?? '';

                        if (tab === 'referencia') {
                          const labelExtra = cargaH < 6 ? '+0,5' : '+1';
                          if (info.tipo === 'normal') {
                            cellLabel = diasSemanaFunc.has(siglaD) ? '' : labelExtra;
                          } else if (info.tipo === 'falta') {
                            cellLabel = '-1';
                          } else if (info.tipo === 'feriado_trabalhado') {
                            cellLabel = labelExtra;
                          }
                        } else if (tab === 'competencia') {
                          if (info.tipo === 'normal' && siglaD === 'SAB' && diasSemanaFunc.has('SAB')) {
                            cellLabel = '0,5';
                          } else if (info.tipo === 'normal') {
                            cellLabel = '1';
                          }
                        }
                        return (
                          <td key={d}
                            title={`${func.nome} — ${new Date(d+'T00:00:00').toLocaleDateString('pt-BR')}`}
                            onClick={() => abrirModalDia(func, d, info)}
                            style={{ background:cor.bg, color:cor.text, border:'1px solid #e5e7eb', textAlign:'center', fontWeight:600, padding:'2px 1px', fontSize:'var(--fs-2xs)', width:22, cursor:'pointer' }}>
                            {cellLabel}
                          </td>
                        );
                      })}
                      <td style={{ textAlign:'center', fontWeight:700, padding:'4px 6px', fontSize:'var(--fs-sm)', borderLeft:'2px solid #bfdbfe', color: totalCor }}>
                        {totalCelula}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── Tabela final de VR ── */}
      {!loading && !erro && itens.length > 0 && tab === 'resumo' && (
        <div className="card-ponto p-0">
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 20px', borderBottom:'1px solid #f1f5f9', background:'#faf5ff' }}>
            <div style={{ width:30, height:30, background:'#ede9fe', border:'1px solid #d8b4fe', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#7c3aed', fontSize:'var(--fs-base)' }}>
              <i className="bi bi-receipt" />
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:'var(--fs-base)', color:'#1e293b' }}>Relatório Final de VR</div>
              <div style={{ fontSize:'var(--fs-2xs)', color:'#7c3aed', fontWeight:500 }}>
                Competência {labelCredito} · Mês Atual {labelRef}
              </div>
            </div>
          </div>

          <div style={{ overflowX:'auto' }}>
            <table className="table-ponto" style={{ width:'100%' }}>
              <thead>
                <tr>
                  {/* ── Funcionário ── */}
                  <TH center={false} style={{ paddingLeft:28, minWidth:200 }}>Funcionário</TH>

                  {/* ── Bloco: Competência ── */}
                  <TH color="#93c5fd" title={`Dias úteis em ${labelCredito}`}>Dias úteis</TH>
                  <TH color="#bfdbfe" title="Dias de férias no mês de competência">Férias</TH>
                  <TH color="#fed7aa" title="Dias de afastamento no mês de competência">Afastam.</TH>
                  <TH color="#6ee7b7" title="Base = Dias úteis − Férias − Afastamentos">Base VR</TH>

                  {/* ── Bloco: Ajustes (ref) ── */}
                  <TH color="#fca5a5" title={`Faltas em ${labelRef} → −1 ticket cada`}>Faltas</TH>
                  <TH color="#fde68a" title={`Falta justificada com jornada <6h em ${labelRef} → −0,5 ticket`}>F. Just. &lt;6h</TH>
                  <TH color="#c4b5fd" title={`Dia extra trabalhado em ${labelRef} → +0,5 ou +1 ticket`}>Extras</TH>

                  {/* ── Total ── */}
                  <TH color="#fbbf24" style={{ borderRight:'none', minWidth:80 }}>Total VR</TH>
                </tr>
              </thead>
              <tbody>
                {itens.map((item, i) => (
                  <tr key={item.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ paddingLeft:28, fontWeight:600, color:'#1e293b', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:240, borderRight:'1px solid #e2e8f0' }}>
                      {item.nome}
                    </td>

                    {/* Dias úteis */}
                    <td style={{ textAlign:'center', color:'var(--text-secondary)', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #e2e8f0' }}>
                      {item.diasUteis}
                    </td>
                    {/* Férias */}
                    <td style={{ textAlign:'center', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #e2e8f0' }}>
                      <Pill val={item.diasFerias} bg="#dbeafe" color="#1d4ed8" />
                    </td>
                    {/* Afastamentos */}
                    <td style={{ textAlign:'center', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #e2e8f0' }}>
                      <Pill val={item.diasAfastamento} bg="#ffedd5" color="#c2410c" />
                    </td>
                    {/* Base VR */}
                    <td style={{ textAlign:'center', fontWeight:700, color:'#059669', fontVariantNumeric:'tabular-nums', borderRight:'2px solid #d1d5db' }}>
                      {item.vrBase}
                    </td>

                    {/* Faltas */}
                    <td style={{ textAlign:'center', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #e2e8f0' }}>
                      <Pill val={item.faltas} bg="#fee2e2" color="#dc2626" prefix="−" />
                    </td>
                    {/* Falta justificada <6h */}
                    <td style={{ textAlign:'center', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #e2e8f0' }}>
                      <Pill val={item.faltasJust} bg="#fef9c3" color="#92400e" prefix="−0,5×" />
                    </td>
                    {/* Extras */}
                    <td style={{ textAlign:'center', fontVariantNumeric:'tabular-nums', borderRight:'2px solid #d1d5db' }}>
                      {item.extrasInteiro > 0 || item.extrasMeio > 0 ? (
                        <span style={{ display:'inline-flex', gap:4 }}>
                          {item.extrasInteiro > 0 && (
                            <span style={{ background:'#ede9fe', color:'#7c3aed', borderRadius:4, padding:'1px 7px', fontWeight:700 }}>
                              +{item.extrasInteiro}
                            </span>
                          )}
                          {item.extrasMeio > 0 && (
                            <span style={{ background:'#ede9fe', color:'#7c3aed', borderRadius:4, padding:'1px 7px', fontWeight:700 }}>
                              +{fmtNum(item.extrasMeio * 0.5)}
                            </span>
                          )}
                        </span>
                      ) : <span style={{ color:'#cbd5e1' }}>—</span>}
                    </td>

                    {/* Total */}
                    <td style={{ textAlign:'center', fontWeight:800, color:'#4f46e5', fontSize:'var(--fs-base)', fontVariantNumeric:'tabular-nums' }}>
                      {fmtNum(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:'#eff6ff', borderTop:'2px solid #bfdbfe' }}>
                  <td style={{ paddingLeft:28, fontWeight:700, color:'#1e293b', borderRight:'1px solid #bfdbfe', fontSize:'var(--fs-xs)' }}>
                    {itens.length} funcionário(s)
                  </td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'var(--text-secondary)', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #bfdbfe' }}>
                    {tot.diasUteis}
                  </td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#1d4ed8', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #bfdbfe' }}>
                    {tot.diasFerias > 0 ? tot.diasFerias : '—'}
                  </td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#c2410c', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #bfdbfe' }}>
                    {tot.diasAfastamento > 0 ? tot.diasAfastamento : '—'}
                  </td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#059669', fontVariantNumeric:'tabular-nums', borderRight:'2px solid #bfdbfe' }}>
                    {tot.vrBase}
                  </td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#dc2626', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #bfdbfe' }}>
                    {tot.faltas > 0 ? `−${tot.faltas}` : '—'}
                  </td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#92400e', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #bfdbfe' }}>
                    {tot.faltasJust > 0 ? `−${fmtNum(tot.faltasJust * 0.5)}` : '—'}
                  </td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#7c3aed', fontVariantNumeric:'tabular-nums', borderRight:'2px solid #bfdbfe' }}>
                    {(tot.extrasInteiro + tot.extrasMeio) > 0
                      ? `+${fmtNum(tot.extrasInteiro + tot.extrasMeio * 0.5)}`
                      : '—'}
                  </td>
                  <td style={{ textAlign:'center', fontWeight:800, color:'#4f46e5', fontSize:'var(--fs-md)', fontVariantNumeric:'tabular-nums' }}>
                    {fmtNum(tot.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ── Legenda das regras ── */}
          <div style={{ padding:'10px 20px', borderTop:'1px solid #f1f5f9', background:'#f8fafc', display:'flex', flexWrap:'wrap', gap:16 }}>
            <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:5 }}>
              <span style={{ background:'#fee2e2', color:'#dc2626', borderRadius:3, padding:'1px 6px', fontWeight:700 }}>F</span>
              Falta → −1 ticket
            </div>
            <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:5 }}>
              <span style={{ background:'#fef9c3', color:'#92400e', borderRadius:3, padding:'1px 6px', fontWeight:700 }}>AT</span>
              Falta justificada com jornada &lt;6h → −0,5 ticket
            </div>
            <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:5 }}>
              <span style={{ background:'#ede9fe', color:'#7c3aed', borderRadius:3, padding:'1px 6px', fontWeight:700 }}>EX</span>
              Dia extra (fim de semana): jornada ≥6h → +1; jornada &lt;6h → +0,5
            </div>
            <div style={{ marginLeft:'auto', fontSize:'var(--fs-2xs)', color:'var(--text-subtle)' }}>
              Fórmula: Base VR − Descontos + Acréscimos
            </div>
          </div>

          {/* ── Criar VR ── */}
          <div style={{ padding:'10px 20px', borderTop:'1px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'flex-end', gap:10 }}>
            {erroCriar && <span style={{ fontSize:'var(--fs-xs)', color:'#dc2626' }}><i className="bi bi-exclamation-circle me-1" />{erroCriar}</span>}
            {vrCriado ? (
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:'var(--fs-xs)', color:'#059669', fontWeight:600 }}>
                  <i className="bi bi-check-circle-fill me-1" />{vrCriado.existente ? 'VR já existente' : 'VR criado com sucesso'}
                </span>
                <button className="btn-secondary-custom" onClick={() => navigate('/relatorio-vr')}>
                  <i className="bi bi-list-ul" /> Ver todos os VRs
                </button>
                <button className="btn-primary-custom" onClick={() => navigate(`/relatorio-vr/${vrCriado.id}`)}>
                  <i className="bi bi-receipt" /> Abrir VR #{vrCriado.id}
                </button>
              </div>
            ) : (
              <button className="btn-primary-custom" onClick={handleCriarVR} disabled={criando}
                style={{ background:'linear-gradient(135deg,#7c3aed,#6d28d9)', boxShadow:'0 4px 14px rgba(124,58,237,0.35)' }}>
                {criando
                  ? <><span className="spinner-border spinner-border-sm" style={{width:10,height:10}} />Salvando...</>
                  : <><i className="bi bi-floppy" /> Salvar VR</>}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Estado vazio ── */}
      {!loading && !erro && dadosCredito && dadosCredito.funcionarios.length === 0 && (
        <div className="empty-state">
          <i className="bi bi-people" />
          <p>Nenhum funcionário encontrado para os filtros selecionados.</p>
        </div>
      )}

      {/* ── Modal Registro do Dia ── */}
      {modalDia && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setModalDia(null)}>
          <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 20px 60px rgba(15,23,42,0.2)', padding:28, minWidth:360, maxWidth:460, width:'100%' }}>

            {/* Cabeçalho — nome em destaque */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:36, height:36, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', color:'#1d4ed8', fontSize:'1rem', flexShrink:0 }}>
                  <i className="bi bi-person-badge" />
                </div>
                <div>
                  <div style={{ fontWeight:800, fontSize:'var(--fs-base)', color:'#1e293b', letterSpacing:'-0.2px' }}>
                    {modalDia.func.nome}
                  </div>
                  <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', marginTop:2 }}>
                    {new Date(modalDia.date + 'T00:00:00').toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
                  </div>
                </div>
              </div>
              <button className="btn-icon" onClick={() => setModalDia(null)} style={{ color:'var(--text-muted)' }}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            {/* Status do dia */}
            <div style={{ marginBottom:16 }}>
              {(() => {
                const tipo = modalDia.info?.tipo;
                const cores = {
                  normal:             { bg:'#f0fdf4', border:'#bbf7d0', color:'#15803d', label:'Normal' },
                  falta:              { bg:'#fef2f2', border:'#fecaca', color:'#dc2626', label:'Falta' },
                  afastamento:        { bg:'#fff7ed', border:'#fed7aa', color:'#c2410c', label:'Afastamento' },
                  ferias:             { bg:'#eff6ff', border:'#bfdbfe', color:'#1d4ed8', label:'Férias' },
                  atestado:           { bg:'#fefce8', border:'#fde68a', color:'#92400e', label:'Atestado' },
                  folga:              { bg:'#f8fafc', border:'#e2e8f0', color:'#64748b', label:'Folga' },
                  feriado:            { bg:'#f0fdf4', border:'#bbf7d0', color:'#065f46', label:'Feriado' },
                  feriado_trabalhado: { bg:'#ecfdf5', border:'#a7f3d0', color:'#065f46', label:'Feriado Trabalhado' },
                };
                const c = cores[tipo] || cores.normal;
                return (
                  <div style={{ background:c.bg, border:`1px solid ${c.border}`, borderRadius:8, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                    <i className="bi bi-calendar-day" style={{ color:c.color, fontSize:'1rem' }} />
                    <span style={{ fontWeight:700, color:c.color, fontSize:'var(--fs-sm)' }}>{c.label}</span>
                  </div>
                );
              })()}
            </div>

            {/* Registro de ponto */}
            <div style={{ background:'#f8fafc', borderRadius:8, padding:'12px 14px', fontSize:'var(--fs-xs)', color:'#1e293b' }}>
              <div style={{ fontWeight:700, marginBottom:8, color:'#334155', display:'flex', alignItems:'center', gap:6 }}>
                <i className="bi bi-clock" style={{ color:'#7c3aed' }} /> Registro de Ponto
              </div>
              {modalLoading ? (
                <div style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text-muted)' }}>
                  <span className="spinner-border spinner-border-sm" style={{ width:12, height:12 }} /> Carregando...
                </div>
              ) : modalReg ? (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 16px' }}>
                  {[
                    ['Entrada',      modalReg.hora_entrada],
                    ['Início almoço',modalReg.hora_inicio_almoco],
                    ['Fim almoço',   modalReg.hora_fim_almoco],
                    ['Saída',        modalReg.hora_saida],
                    ['Horas trab.',  modalReg.horas_trabalhadas != null ? `${Number(modalReg.horas_trabalhadas).toFixed(1)}h` : null],
                    ['Status',       modalReg.status],
                  ].map(([label, val]) => val ? (
                    <div key={label} style={{ display:'flex', flexDirection:'column' }}>
                      <span style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px' }}>{label}</span>
                      <span style={{ fontWeight:600, color:'#1e293b' }}>{val}</span>
                    </div>
                  ) : null)}
                </div>
              ) : (
                <div style={{ color:'var(--text-muted)', fontStyle:'italic' }}>Nenhum registro encontrado para este dia.</div>
              )}
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:18 }}>
              <button className="btn-secondary-custom" onClick={() => setModalDia(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
